/**
 * Formatters Utility
 * Provides helper functions for formatting data throughout the application
 * including payment descriptions, phone numbers, currency, etc.
 */

/**
 * Format payment description with product name and plan duration
 * Creates readable payment descriptions like:
 * - "Tirzepatide Injection" (monthly)
 * - "Tirzepatide Injection (3 Month Bundle)" (quarterly)
 * - "Sermorelin Therapy (6 Month Bundle)"
 * 
 * @param {Object} funnelData - The funnel data object containing product and plan info
 * @param {string} funnelData.selected_product_name - Product name (e.g., "Tirzepatide Injection")
 * @param {string} [funnelData.selected_plan] - Plan name (e.g., "monthly", "quarterly")
 * @param {number} [funnelData.selected_plan_months] - Plan duration in months (e.g., 1, 3, 6, 12)
 * @returns {string} Formatted payment description
 * 
 * @example
 * // Monthly plan
 * formatPaymentDescription({ selected_product_name: "Tirzepatide Injection", selected_plan: "monthly" })
 * // Returns: "Tirzepatide Injection"
 * 
 * @example
 * // Quarterly plan
 * formatPaymentDescription({ selected_product_name: "Tirzepatide Injection", selected_plan_months: 3 })
 * // Returns: "Tirzepatide Injection (3 Month Bundle)"
 */
export const formatPaymentDescription = (funnelData) => {
  // Extract product name with fallback
  const productName = funnelData?.selected_product_name || 'Prescription'
  
  // Extract plan information
  const plan = funnelData?.selected_plan
  // Convert to number to handle both string and number values
  const planMonths = funnelData?.selected_plan_months ? Number(funnelData.selected_plan_months) : null
  
  // Determine the plan duration suffix
  let planSuffix = ''
  
  // PRIORITY: Use planMonths if available (more accurate than plan name)
  if (planMonths === 1) {
    // Monthly plan - no suffix needed (cleaner look)
    planSuffix = ''
  } else if (planMonths === 3) {
    planSuffix = ' (3 Month Bundle)'
  } else if (planMonths === 6) {
    planSuffix = ' (6 Month Bundle)'
  } else if (planMonths === 12) {
    planSuffix = ' (12 Month Bundle)'
  } else if (planMonths && planMonths > 1) {
    // Generic fallback for other month values
    planSuffix = ` (${planMonths} Month Bundle)`
  } else if (plan === 'monthly') {
    // Fallback to plan name if planMonths not available
    planSuffix = ''
  } else if (plan === 'quarterly') {
    planSuffix = ' (3 Month Bundle)'
  }
  
  // Return formatted description: "Product Name" or "Product Name (X Month Bundle)"
  return `${productName}${planSuffix}`
}

/**
 * Format phone number for display in UI
 * Converts phone numbers to (XXX) XXX-XXXX format
 * 
 * @param {string} value - Phone number in any format
 * @returns {string} Phone number formatted as (XXX) XXX-XXXX
 * 
 * @example
 * formatPhoneNumber("5551234567")
 * // Returns: "(555) 123-4567"
 * 
 * @example
 * formatPhoneNumber("+15551234567")
 * // Returns: "(555) 123-4567"
 */
export const formatPhoneNumber = (value) => {
  if (!value) return ''
  
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '')
  
  // Handle partial numbers during typing
  if (phoneNumber.length <= 3) return phoneNumber
  if (phoneNumber.length <= 6) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`
  }
  
  // Full format: (XXX) XXX-XXXX
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`
}

/**
 * Format phone number to E.164 format for API/payment processing
 * Converts various phone number formats to +1XXXXXXXXXX
 * 
 * @param {string} phone - Phone number in any format
 * @returns {string} Phone number in E.164 format (+1XXXXXXXXXX)
 * 
 * @example
 * formatPhoneForPayment("(555) 123-4567")
 * // Returns: "+15551234567"
 */
export const formatPhoneForPayment = (phone) => {
  if (!phone) return ''
  
  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '')
  
  // If it already starts with country code (11 digits for US), add +
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`
  }
  
  // If it's 10 digits (US number without country code), add +1
  if (digits.length === 10) {
    return `+1${digits}`
  }
  
  // Default: assume US number and add +1
  return `+1${digits}`
}
